"use client";

import { useInspectorMode } from "@utils/ContentfulUtils";
import cn from "@utils/cn";
import { encodeUrlIfNeeded } from "@utils/string";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { FC, useEffect, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import ButtonOrVideoModal from "@/components/ui/ButtonOrVideoModal/ButtonOrVideoModal";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { CustomLink, MediaComponentProps, MediaComponentType, VideoModal } from "@/types";

const wrapperAnimation = {
  "exit-animation": { width: "calc(100% * 22/24)" },
  "full-width": { width: "100%" },
};

const childrenAnimation = {
  "exit-animation": { height: "calc(100% * 22/24)" },
  "full-width": { height: "100%" },
};

const Media: FC<MediaComponentProps> = ({ entry, isInventoryAvaialable }) => {
  const inspectorMode = useInspectorMode(entry.id);
  const t = useTranslations();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const container = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isFullWidth, setIsFullWidth] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const gradientBg = entry.gradientColor?.value?.replace(/rgb/i, "rgba").replace(/\)/i, ",0.4)");
  const [videoLoaded, setVideoLoaded] = useState(false);
  const togglePlay = () => {
    if (videoRef.current?.paused) {
      videoRef.current?.play();
      setIsPlaying(true);
    } else {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
    ctaClickTracking(isPlaying ? "pause" : "play", "Media", "Media");
  };

  const observeComponent = () => {
    if (container.current) {
      let callback = (entries: any[], observer: any) => {
        entries.forEach((entry) => {
          if (entry.boundingClientRect.top < 0) {
            if (entry.isIntersecting) {
              // scroll direction up
              setIsFullWidth(true);
            } else {
              //  scroll direction down
              setIsFullWidth(false);
            }
          }
        });
      };
      const observer = new IntersectionObserver(callback, { threshold: 0.9 });
      observer.observe(container.current);
    }
  };

  useEffect(() => {
    // Workaround to correctly render mobile or desktop video according to device
    if (entry.video && videoRef.current) {
      // TODO @fraoteri da capire questa gestione che causa errore AbortError https://goo.gl/LdLk22
      videoRef.current.load();
      setVideoLoaded(true);
      videoRef.current.play();
    }
  }, [isDesktop, entry.video]);

  useEffect(() => {
    if (container.current && entry.variant === MediaComponentType.FULL_SCREEN) {
      observeComponent();
    }
  }, [container, entry]);

  const mediaContent = () => {
    let videoClasses = isDesktop ? "aspect-16-9" : "aspect-9-16";
    if (entry.variant === MediaComponentType.VERTICAL) {
      videoClasses = "aspect-3-4";
    }
    return (
      <>
        <div className="x-ray" {...inspectorMode.getProps(entry.gradientColor?.value)}>
          <style jsx>{`
            .x-ray::after {
              ${entry.gradientColor && `background-color:${gradientBg}`};
            }
          `}</style>
          <div className={cn("rounded-xl overflow-hidden relative", videoClasses)}>
            {entry.video ? (
              <>
                <video
                  ref={videoRef}
                  className={`w-full object-cover absolute top-0 left-0 h-full ${videoLoaded ? "block" : "hidden"}`}
                  poster={entry.backupVideoImage?.url}
                  //poster={`/_next/image?url=${encodeUrlIfNeeded(entry.backupVideoImage?.url)}&q=75&w=1024`}
                  muted
                  // autoPlay
                  loop
                  playsInline
                  {...inspectorMode.getProps(entry.backupVideoImage?.url)}
                >
                  <source src={isDesktop ? entry.video.url : entry.mobileVideo ? entry.mobileVideo?.url : entry.video.url} type="video/mp4" />
                </video>
                {!(entry.ctaMediaBanner as CustomLink)?.link?.url && (
                  <Button
                    size={ButtonSize.SMALL}
                    customClass="md:flex items-center border-none w-fit h-fit p-xs font-medium box-material-primary backdrop-blur-xl overflow-hidden absolute bottom-l right-l rounded-3xl"
                    onClick={togglePlay}
                  >
                    <Icon name={isPlaying ? "pause" : "play"} size="24" title="Pause" className="stroke-white" />
                  </Button>
                )}
              </>
            ) : (
              entry.image?.url &&
              entry.mobileImage?.url && (
                <Image
                  src={isDesktop ? entry.image.url : entry.mobileImage.url}
                  width={1094}
                  height={615}
                  className={cn("aspect-9-16 md:aspect-16-9 w-full rounded-xl", entry.variant === MediaComponentType.VERTICAL && "aspect-3-4")}
                  alt={(isDesktop ? entry.image.description || entry.image.title : entry.mobileImage.description || entry.mobileImage.title) || ""}
                  {...inspectorMode.getProps(entry.image.url)}
                />
              )
            )}
            {entry.ctaMediaBanner && (
              <ButtonOrVideoModal
                cta={entry.ctaMediaBanner}
                btnClasses="absolute top-[50%] translate-y-[-50%] translate-x-[-50%] left-[50%] !box-material-primary backdrop-blur-[30px] font-medium py-m px-l w-max border-none !text-white overflow-hidden rounded-3xl"
                showIcon
                modalTitle={entry.modalLabel}
                isInventoryAvaialable={isInventoryAvaialable}
              />
            )}
          </div>
        </div>
        {entry.variant === MediaComponentType.HORIZONTAL && entry.episodeTitle && entry.podcastTitle && (
          <div className="text-center mt-l md:flex md:flex-row-reverse md:justify-between">
            <div className="font-primary text-s uppercase" {...inspectorMode.getProps(entry.episodeTitle)}>
              {entry.episodeTitle}
            </div>
            <div className="font-primary font-medium text-m uppercase mt-m md:mt-0" {...inspectorMode.getProps(entry.episodeTitle)}>
              {entry.podcastTitle}
            </div>
          </div>
        )}
      </>
    );
  };

  if (entry.variant === MediaComponentType.FULL_SCREEN) {
    return (
      <div className="w-full pt-xl">
        <motion.div
          ref={container}
          className="w-dvw md:w-full h-svh  mx-auto relative"
          variants={wrapperAnimation}
          animate={isFullWidth ? "full-width" : "exit-animation"}
          initial="full-width"
          transition={{ ease: "easeInOut", duration: 0.3, delay: 0.03 }}
        >
          {entry.video ? (
            <motion.div
              className="h-svh w-full object-cover relative"
              variants={childrenAnimation}
              animate={isFullWidth ? "full-width" : "exit-animation"}
              initial="full-width"
              transition={{ ease: "easeInOut", duration: 0.3, delay: 0.03 }}
            >
              <video ref={videoRef} className="h-full w-full object-cover" poster={entry.backupVideoImage?.url} muted autoPlay loop playsInline>
                <source src={isDesktop ? entry.video.url : entry.mobileVideo ? entry.mobileVideo?.url : entry.video.url} type="video/mp4" />
              </video>
              {entry.ctaMediaBanner && (
                <ButtonOrVideoModal
                  cta={entry.ctaMediaBanner}
                  btnClasses="absolute top-[50%] translate-y-[-50%] translate-x-[-50%] left-[50%] !box-material-primary backdrop-blur-[30px] font-medium py-m px-l w-max border-none !text-white overflow-hidden rounded-3xl"
                  showIcon
                />
              )}
              <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.SECONDARY}
                theme={ButtonTheme.WHITE}
                customClass="md:flex items-center border-none w-fit h-fit p-xs font-medium !box-material-primary !backdrop-blur-xl overflow-hidden absolute bottom-l right-l"
                onClick={togglePlay}
              >
                <Icon name={isPlaying ? "pause" : "play"} size="24" title="Pause" className="stroke-white" />
              </Button>
            </motion.div>
          ) : entry.image ? (
            <>
              <motion.div
                className="absolute h-full w-full top-0 left-0 bg-cover"
                style={{ backgroundImage: `url(${isDesktop ? entry.image.url : entry.mobileImage ? entry.mobileImage.url : entry.image.url})` }}
              />
              {entry.ctaMediaBanner && (
                <ButtonOrVideoModal
                  cta={entry.ctaMediaBanner}
                  btnClasses="absolute top-[50%] translate-y-[-50%] translate-x-[-50%] left-[50%] !box-material-primary backdrop-blur-[30px] font-medium py-m px-l w-max border-none !text-white overflow-hidden rounded-3xl"
                  showIcon
                />
              )}
            </>
          ) : (
            <></>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...(entry.backgroundColor && { backgroundColor: entry.backgroundColor }),
        ...(entry.background && { backgroundImage: `url(${entry.background.url})` }),
      }}
      className="py-xxl md:py-xxl  md:pb-4xl"
      {...inspectorMode.getProps(entry?.backgroundColor)}
    >
      <div className="w-22\/24 md:w-20\/24 mx-auto ">
        <div className={`${entry.variant === MediaComponentType.VERTICAL && isDesktop ? "w-[50%] mx-auto" : ""}`}>
          <>{mediaContent()}</>
        </div>
      </div>
    </div>
  );
};

export default Media;
